const LIDO_FONT_ORIGIN =
  "https://lidojs-fonts.s3.us-east-2.amazonaws.com";

const FALLBACK_FONT_NAME = "Exo 2";
const FALLBACK_FONT_SOURCES = [
  {
    urls: ["https://fonts.googleapis.com/css2?family=Exo+2:wght@400&display=swap"],
  },
  {
    style: "Bold",
    urls: ["https://fonts.googleapis.com/css2?family=Exo+2:wght@700&display=swap"],
  },
  {
    style: "Italic",
    urls: ["https://fonts.googleapis.com/css2?family=Exo+2:ital@1&display=swap"],
  },
  {
    style: "Bold_Italic",
    urls: ["https://fonts.googleapis.com/css2?family=Exo+2:ital,wght@1,700&display=swap"],
  },
] as const;

export const isLidoFontUrl = (url: string) =>
  url === LIDO_FONT_ORIGIN || url.startsWith(`${LIDO_FONT_ORIGIN}/`);

/**
 * Families Lido hosted on S3 that Google still serves under the same name.
 * Kept in sync with the designer's copy in frontend-2 so the editor and the
 * screen resolve a legacy design to the same typeface.
 */
const GOOGLE_FONT_FAMILY_NAMES =
  [
    "Alegreya",
    "Anton",
    "Arimo",
    "Barlow",
    "Bebas Neue",
    "Cabin",
    "Dosis",
    "Exo 2",
    "Fira Sans",
    "Inter",
    "Josefin Sans",
    "Karla",
    "Lato",
    "Lora",
    "Merriweather",
    "Montserrat",
    "Mukta",
    "Nunito",
    "Nunito Sans",
    "Open Sans",
    "Oswald",
    "Playfair Display",
    "Poppins",
    "PT Sans",
    "PT Serif",
    "Quicksand",
    "Raleway",
    "Roboto",
    "Roboto Condensed",
    "Roboto Mono",
    "Roboto Slab",
    "Rubik",
    "Source Sans Pro",
    "Titillium Web",
    "Ubuntu",
    "Work Sans",
  ];

const RECOVERABLE_GOOGLE_FONTS = new Set(
  GOOGLE_FONT_FAMILY_NAMES.map((name) => name.toLowerCase()),
);

export const getCanonicalGoogleFontFamily = (name: string) =>
  GOOGLE_FONT_FAMILY_NAMES.find(
    (family) => family.toLowerCase() === name.trim().toLowerCase(),
  ) || name.trim();

const googleSourceUrl = (name: string, style?: unknown) => {
  const family = name.trim().replace(/\s+/g, "+");
  const variant = typeof style === "string" ? style.toLowerCase() : "";
  const isBold = variant.includes("bold");
  const isItalic = variant.includes("italic");

  const axes = isItalic
    ? `ital,wght@1,${isBold ? 700 : 400}`
    : `wght@${isBold ? 700 : 400}`;

  return `https://fonts.googleapis.com/css2?family=${family}:${axes}&display=swap`;
};

const googleFamilyUrl = (name: string) =>
  `https://fonts.googleapis.com/css2?family=${name.trim().replace(/\s+/g, "+")}&display=swap`;

const isRecoverableGoogleFont = (name: string) =>
  RECOVERABLE_GOOGLE_FONTS.has(name.trim().toLowerCase());

const CSS_FONT_FORMATS: Record<string, string> = {
  eot: "embedded-opentype",
  otf: "opentype",
  ttf: "truetype",
};

/**
 * Escapes a font family for use inside a single-quoted CSS string, so names
 * containing an apostrophe (e.g. "Baloo Bhaijaan's") don't break the rule.
 */
export const escapeCssFontFamily = (name: string) =>
  name.replace(/\\/g, "\\\\").replace(/'/g, "\\'");

/**
 * Maps a file extension or API-declared format to the keyword `src: format()`
 * expects. Falls back to the URL's extension, then to woff2.
 */
export const getCssFontFormat = (url: string, declared?: string) => {
  const format = declared?.replace(/^\./, "").toLowerCase();
  if (format) return CSS_FONT_FORMATS[format] ?? format;

  // Only the final path segment can carry an extension - splitting the whole
  // URL on "." would treat the hostname's dots as one.
  const path = url.split(/[?#]/, 1)[0];
  const segment = path.slice(path.lastIndexOf("/") + 1);
  const dot = segment.lastIndexOf(".");
  const extension = dot === -1 ? "" : segment.slice(dot + 1).toLowerCase();

  if (!extension) return "woff2";
  return CSS_FONT_FORMATS[extension] ?? extension;
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isLegacyLidoFont = (value: unknown): value is UnknownRecord => {
  if (
    !isRecord(value) ||
    typeof value.name !== "string" ||
    !Array.isArray(value.fonts)
  ) {
    return false;
  }

  return value.fonts.some((source) => {
    if (!isRecord(source) || !Array.isArray(source.urls)) return false;
    return source.urls.some(
      (url) => typeof url === "string" && isLidoFontUrl(url),
    );
  });
};

const isEmptyLegacyFont = (value: unknown): value is UnknownRecord =>
  isRecord(value) &&
  typeof value.name === "string" &&
  Array.isArray(value.fonts) &&
  value.fonts.length === 0;

const collectLegacyFontNames = (value: unknown, names: Set<string>) => {
  if (isLegacyLidoFont(value) || isEmptyLegacyFont(value)) {
    names.add(String(value.name).toLowerCase());
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectLegacyFontNames(item, names));
  } else if (isRecord(value)) {
    Object.values(value).forEach((item) => collectLegacyFontNames(item, names));
  }
};

const fallbackFontData = () => ({
  name: FALLBACK_FONT_NAME,
  fonts: FALLBACK_FONT_SOURCES.map((source) => ({
    ...source,
    urls: [...source.urls],
  })),
});

/**
 * Rewrites a legacy font's S3 urls to Google, keeping its name and the exact
 * set of styles it declared. Undefined when the family is not recoverable.
 */
const recoverGoogleFontData = (value: UnknownRecord) => {
  const name = String(value.name);
  if (!isRecoverableGoogleFont(name)) return undefined;

  const sources = Array.isArray(value.fonts) ? value.fonts : [];
  if (sources.length === 0) {
    return {
      name,
      fonts: [{ urls: [googleFamilyUrl(name)] }],
    };
  }

  const recovered = sources.reduce<Array<{ style?: unknown; urls: string[] }>>(
    (acc, source) => {
      if (!isRecord(source)) return acc;
      acc.push(
        source.style === undefined
          ? { urls: [googleSourceUrl(name, undefined)] }
          : { style: source.style, urls: [googleSourceUrl(name, source.style)] },
      );
      return acc;
    },
    [],
  );

  if (recovered.length === 0) return undefined;
  return { name, fonts: recovered };
};

const replaceLegacyFonts = (value: unknown, names: Set<string>): unknown => {
  if (isLegacyLidoFont(value) || isEmptyLegacyFont(value)) {
    return recoverGoogleFontData(value) ?? fallbackFontData();
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceLegacyFonts(item, names));
  }
  if (!isRecord(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => {
      if (
        key === "fontFamily" &&
        typeof item === "string" &&
        names.has(item.toLowerCase())
      ) {
        return [key, FALLBACK_FONT_NAME];
      }
      return [key, replaceLegacyFonts(item, names)];
    }),
  );
};

export const replaceLegacyLidoFonts = <T>(value: T): T => {
  const names = new Set(getLegacyLidoFontNames(value));
  if (names.size === 0) return value;

  // Recovered families keep their own name, so only unrecoverable ones have
  // their fontFamily attrs repointed at Exo 2.
  const substituted = new Set(
    Array.from(names).filter((name) => !isRecoverableGoogleFont(name)),
  );

  return replaceLegacyFonts(value, substituted) as T;
};

export const getLegacyLidoFontNames = (value: unknown) => {
  const legacyFontNames = new Set<string>();
  collectLegacyFontNames(value, legacyFontNames);
  return Array.from(legacyFontNames).sort();
};
